<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <h1 class="title mr-3">{{ $t('src/pages/courses.748') }}</h1>
        <button v-if="!isNeuroAdmin && !isReadonly" class="btn btn-link btn-with-icon" @click="createCourse">
          <span class="icon icon-plus"></span>{{ $t('button.createCourse') }}
        </button>
      </div>
      <div class="d-flex align-items-center">
        <course-filter
          :title="$t('src/components/filters/pagecoursefilter.courseFilters')"
          :person="filteredPerson"
          :id="courseId"
          :statuses="statuses"
          :device="device"
          :dates="dates"
          @confirm="confirmFilter"
        >
        </course-filter>
      </div>
    </div>
    <filter-result
      v-if="filterResultData && isReady"
      :data="filterResultData"
      :headers="headers"
    ></filter-result>
    <section>
      <base-table
        :headers="headers"
        :no-data="noCourses"
        :loading="loading"
        :sorting="sorting"
        @filter="filterByTableSelect"
        @sorting="changeSorting"
        @isReady="isReadyTable"
      >
        <template #tbody>
          <course-table-row
            v-for="course in coursesPart"
            :key="course.id"
            :course="course"
          ></course-table-row>
        </template>
      </base-table>
      <div class="mt-3">
        <paginator
          v-if="showPaginator"
          :total="totalCount"
          :per-page="perPage"
          v-model="currentPage"
          @change:count="numberOfElements"
        ></paginator>
      </div>
    </section>
  </div>
</template>

<script>
import http from '@/modules/http.js'
import { stringify } from 'qs'
import { status, DATE_FORMAT, FIND_BY_PERSON } from '@/main.config.js'
import { paginator } from '@/mixins/paginator.js'
import { userRights } from '@/mixins/user-rights.js'
import { getStatusListForFilter } from '@/modules/status-list.js'
import { pageFiltersInQuery } from '@/components/Filters/mixins/page-filters-in-query'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

export default {
  name: 'CoursesList',
  components: {
    CourseTableRow: () => import('@/components/table-rows/CourseTableRow.vue'),
    BaseTable: () => import('@/base/BaseTable.vue'),
    CourseFilter: () => import('@/components/Filters/PageCourseFilter.vue'),
    FilterResult: () => import('@/components/Filters/FilterResult.vue')
  },
  mixins: [paginator, userRights, pageFiltersInQuery, systemUnavailable],
  data: () => ({
    loading: true,
    coursesPart: [],
    filteredPerson: {
      id: null
    },
    courseId: undefined,
    statuses: [status.active],
    device: undefined,
    dates: null,
    sorting: {
      by: 'date',
      order: 'desc'
    },
    isReady: false,
    numberDevice: null
  }),
  mounted() {
    if (this.$route.params.device) {
      this.device = this.$route.params.device
      this.numberDevice = this.device[0].serial_number
    }
    this.loadData()
  },
  computed: {
    statusList() {
      return getStatusListForFilter('course')
    },
    hasFilterRange() {
      return this.dates && this.dates.startDate && this.dates.endDate
    },
    hasSelectedDevice() {
      return this.device?.length > 0
    },
    sortParams() {
      const sortOrder = this.sorting.order === 'asc' ? 1 : -1
      if (this.sorting.by === 'date') {
        return {
          start_date: sortOrder,
          close_date: sortOrder
        }
      } else if (this.sorting.by === 'patient') {
        return {
          patient_surname: sortOrder,
          patient_name: sortOrder
        }
      } else if (this.sorting.by === 'doctor') {
        return {
          doctor_surname: sortOrder,
          doctor_name: sortOrder
        }
      }
      return {
        id: sortOrder
      }
    },
    params() {
      const params = { ...this.pageParams, $sort: this.sortParams }

      if (this.statuses.length === 1) {
        params['course.status'] = this.statuses[0]
      } else if (this.statuses.length > 1) {
        params['$or'] = this.statuses.map(item => ({ 'course.status': item }))
      } else if (Object.prototype.hasOwnProperty.call(params, 'course.status')) {
        delete params['course.status']
      }

      if (this.filteredPerson.id) {
        if (this.filteredPerson.findBy === FIND_BY_PERSON.patient) {
          params.patient_id = this.filteredPerson.id
          if (Object.prototype.hasOwnProperty.call(params, 'doctor_id')) {
            delete params['doctor_id']
          }
        } else if (this.filteredPerson.findBy === FIND_BY_PERSON.doctor) {
          params.doctor_id = this.filteredPerson.id
          if (Object.prototype.hasOwnProperty.call(params, 'patient_id')) {
            delete params['patient_id']
          }
        }
      } else {
        if (Object.prototype.hasOwnProperty.call(params, 'patient_id')) {
          delete params['patient_id']
        }
        if (Object.prototype.hasOwnProperty.call(params, 'doctor_id')) {
          delete params['doctor_id']
        }
      }
      if (this.hasSelectedDevice) {
        if (this.device.length === 1) {
          params['device_id'] = this.device[0].id
        } else if (this.device.length > 1) {
          params['$or'] = this.device.map(item => ({
            device_id: item.id
          }))
        } else if (Object.prototype.hasOwnProperty.call(params, 'device_id')) {
          delete params['device_id']
        }
      }
      if (this.$store.getters.IS_SELECTED_HOSPITAL) {
        params['user.hospital_id'] = this.$store.getters.SELECTED_HOSPITAL
      }

      if (this.courseId) {
        params['course.id'] = this.courseId
      }

      if (this.hasFilterRange) {
        params['course.start_date'] = {
          $gte: this.dates.startDate.format(DATE_FORMAT.server),
          $lte: this.dates.endDate.format(DATE_FORMAT.server)
        }
      }

      return params
    },
    filterResultData() {
      const data = {}

      if (this.statuses.length > 0) {
        data[this.$t('general.courseStatus')] = this.statuses.map(item => this.statusList[item].title)
      }

      if (this.hasFilterRange) {
        data[this.$t('general.dates')] =
          this.dates.startDate.format(DATE_FORMAT.ruShort) +
          '-' +
          this.dates.endDate.format(DATE_FORMAT.ruShort)
      }

      if (this.courseId) {
        data['ID'] = this.courseId
      }

      if (this.filteredPerson.id) {
        const title =
          this.filteredPerson.findBy === FIND_BY_PERSON.patient
            ? this.$t('entity.patient')
            : this.$t('src/components/courseaddform.4348')
        data[title] = this.filteredPerson.name
      }
      if (this.hasSelectedDevice && this.numberDevice) {
        data[this.$t('entity.device')] = this.numberDevice
      }

      return data
    },
    noCourses() {
      return this.coursesPart.length === 0
    },
    headers() {
      return {
        patient: {
          title: this.$t('entity.patient'),
          class: 'th-name',
          sorting: 'asc'
        },
        date: {
          title: this.$t('general.dates'),
          class: 'th-date',
          sorting: 'desc'
        },
        doctor: {
          title: this.$t('src/components/courseaddform.4348'),
          class: 'th-name-short',
          sorting: 'asc'
        },
        status: {
          title: this.$t('general.courseStatus'),
          class: 'th-mid',
          filter: true,
          options: this.statusList,
          selected: this.statuses.length === 1 ? this.statuses[0] : null
        },
        device: {
          title: this.$t('entity.device')
        },
        lastSession: {
          title: this.$t('general.lastSession'),
          class: 'th-date'
        },
        id: {
          title: 'ID',
          sorting: 'asc'
        },
        newMessage: {
          title: ''
        }
      }
    }
  },
  methods: {
    loadData() {
      this.loading = true
      this.isReady = false
      http
        .get('/courses', {
          params: this.params,
          paramsSerializer: params =>
            stringify(params, {
              arrayFormat: 'indices',
              encodeValuesOnly: true
            })
        })
        .then(response => response.data)
        .then(courses => {
          this.totalCount = courses.total
          this.coursesPart = courses.data
        })
        .catch(error => console.log(error))
        .finally(() => (this.loading = false))
    },
    confirmFilter(data) {
      this.courseId = data.courseId
      this.filteredPerson = data.person
      this.statuses = data.statuses
      this.dates = data.dates
      this.device = data.device
      this.numberDevice = data.deviceNumber
      this.currentPage = 1
      this.updateFullQuery()
      this.loadData()
    },
    filterByTableSelect(event) {
      if (event.key === 'status') {
        const status = parseInt(event.value, 10)
        this.statuses = status !== 0 ? [status] : []
      } else {
        return
      }
      this.currentPage = 1
      this.updateFullQuery()
      this.loadData()
    },
    changeSorting(sortingData) {
      this.sorting = sortingData
      this.loadData()
    },
    numberOfElements(number) {
      this.perPage = number
    },
    isReadyTable(value) {
      this.isReady = value
    },
    createCourse() {
      if (Number(localStorage.getItem('activeHospital'))) {
        this.$router.push({ name: 'course-add' })
      } else {
        this.$emit('system:unavailable')
      }
    }
  }
}
</script>
